import React, { useEffect, useRef, useState } from 'react';
import EditorJS from '@editorjs/editorjs';
import Paragraph from '@editorjs/paragraph';
import Marker from '@editorjs/marker';
import InlineCode from '@editorjs/inline-code';
import Underline from '@editorjs/underline';

/**
 * EditorJS Text Editor Component
 * @param {Array} text - Array of localized text [{language: 'it', text: 'content'}, ...]
 * @param {string} language - Current language
 * @param {function} onChange - Callback when content changes
 * @param {object} colors - Color scheme
 * @param {string} placeholder - Placeholder text
 * @param {boolean} readOnly - Read-only mode
 */
const EditorJSTextEditor = ({ 
  text = [], 
  language = 'it', 
  onChange, 
  colors = {}, 
  placeholder = 'Inserisci il testo della domanda...',
  readOnly = false 
}) => {
  const editorRef = useRef(null);
  const editorInstanceRef = useRef(null);
  const [isReady, setIsReady] = useState(false);

  // Helper function to get text in current language
  const getCurrentText = () => {
    const currentTextObj = text.find(t => t.language === language);
    return currentTextObj?.text || '';
  };

  // Helper function to parse EditorJS data from text
  const parseEditorData = (textContent) => {
    if (!textContent) {
      return {
        time: Date.now(),
        blocks: [{
          id: Math.random().toString(36).substr(2, 9),
          type: "paragraph",
          data: {
            text: ""
          }
        }],
        version: "2.30.8"
      };
    }

    // Check if it's already EditorJS JSON format
    try {
      const parsed = JSON.parse(textContent);
      if (parsed.blocks && Array.isArray(parsed.blocks)) {
        return parsed;
      }
    } catch (e) {
      // Not JSON, treat as plain text
    }

    // Convert plain text to EditorJS format - combine all lines into a single paragraph
    const cleanText = textContent.replace(/\n+/g, '<br>').trim();
    
    return {
      time: Date.now(),
      blocks: [{
        id: Math.random().toString(36).substr(2, 9),
        type: "paragraph",
        data: {
          text: cleanText || ""
        }
      }],
      version: "2.30.8"
    };
  };

  // Initialize Editor.js
  useEffect(() => {
    if (!editorRef.current || editorInstanceRef.current) return;

    const currentTextContent = getCurrentText();
    const initialData = parseEditorData(currentTextContent);

    const editor = new EditorJS({
      holder: editorRef.current,
      readOnly: readOnly,
      placeholder: placeholder,
      data: initialData,
      tools: {
        // Solo paragraph come block tool
        paragraph: {
          class: Paragraph,
          inlineToolbar: ['bold', 'italic', 'underline', 'marker', 'inlineCode'],
        },
        // Inline tools per la formattazione del testo
        marker: {
          class: Marker,
        },
        inlineCode: {
          class: InlineCode,
        },
        underline: Underline,
      },
      // Configurazioni per nascondere la toolbar principale
      toolbox: {
        opened: false
      },
      onChange: async (api, event) => {
        if (onChange && !readOnly) {
          try {
            const outputData = await api.saver.save();
            
            // Update the text array with new content
            const updatedText = [...text];
            const currentIndex = updatedText.findIndex(t => t.language === language);
            
            if (currentIndex >= 0) {
              updatedText[currentIndex] = {
                ...updatedText[currentIndex],
                text: JSON.stringify(outputData)
              };
            } else {
              updatedText.push({
                language: language,
                text: JSON.stringify(outputData)
              });
            }
            
            onChange(updatedText);
          } catch (error) {
            console.error('Saving failed: ', error);
          }
        }
      }
    });

    editorInstanceRef.current = editor;

    editor.isReady.then(() => {
      setIsReady(true);
      
      // Nascondi la toolbar principale via CSS
      const editorElement = editorRef.current;
      if (editorElement) {
        const style = document.createElement('style');
        style.innerHTML = `
          .codex-editor__redactor {
            padding-bottom: 0 !important;
          }
          .ce-toolbar {
            display: none !important;
          }
          .ce-block {
            margin: 0 !important;
            padding: 0 !important;
          }
          .ce-paragraph {
            line-height: 1.5 !important;
            margin: 0 !important;
            padding: 8px 0 !important;
            text-align: left !important;
          }
          .ce-paragraph[data-placeholder]:empty::before {
            color: #9ca3af;
            font-style: italic;
            text-align: left !important;
          }
          .codex-editor {
            text-align: left !important;
          }
          .codex-editor .ce-block__content {
            text-align: left !important;
            max-width: none !important;
            width: 100% !important;
            margin: 0 !important;
          }
          .ce-block__content {
            max-width: none !important;
            width: 100% !important;
            margin: 0 !important;
          }
          .codex-editor__redactor {
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          
          /* Stili aggiuntivi per forzare larghezza completa */
          .ce-block__content,
          .ce-paragraph {
            max-width: none !important;
            width: 100% !important;
            box-sizing: border-box !important;
          }
          
          .codex-editor .ce-block {
            max-width: none !important;
            width: 100% !important;
          }
          
          .codex-editor {
            width: 100% !important;
          }
        `;
        editorElement.appendChild(style);
      }
    }).catch((reason) => {
      console.error('Editor.js initialization failed because of', reason);
    });

    return () => {
      if (editorInstanceRef.current && editorInstanceRef.current.destroy) {
        editorInstanceRef.current.destroy();
        editorInstanceRef.current = null;
      }
      setIsReady(false);
    };
  }, []);

  // Update editor content when language changes
  useEffect(() => {
    if (isReady && editorInstanceRef.current) {
      const currentTextContent = getCurrentText();
      const newData = parseEditorData(currentTextContent);
      
      editorInstanceRef.current.render(newData).catch((error) => {
        console.error('Failed to update editor content:', error);
      });
    }
  }, [language, isReady]);

  return (
    <div>
      <div 
        ref={editorRef}
        id={`editor-${Math.random().toString(36).substr(2, 9)}`}
        style={{
          minHeight: '60px', // Ridotto da 100px a 60px
          border: `1px solid ${colors.secondary || '#ccc'}`,
          borderRadius: '4px',
          padding: '8px', // Ridotto padding
          backgroundColor: readOnly ? '#f8f9fa' : '#ffffff',
          fontSize: '14px', // Font size più piccolo
          textAlign: 'left', // Allineamento a sinistra
          width: '100%' // Forza larghezza completa
        }}
      />
      {!isReady && (
        <div className="text-muted text-center mt-2">
          <small>Caricamento editor...</small>
        </div>
      )}
    </div>
  );
};

export default EditorJSTextEditor; 